class JitStoreLocatorMvp extends HTMLElement {
  async connectedCallback() {
    this.datasetUrl = this.dataset.datasetUrl;
    this.googleMapsApiKey = this.dataset.googleMapsApiKey;
    this.maxResults = Number(this.dataset.maxResults || 10);
    this.pageSize = Number(this.dataset.pageSize || this.maxResults || 10);
    this.mapEl = this.querySelector("[data-locator-map]");
    this.resultsEl = this.querySelector("[data-locator-results]");
    this.statusEl = this.querySelector("[data-locator-status]");
    this.summaryEl = this.querySelector("[data-locator-summary]");
    this.formEl = this.querySelector("[data-locator-form]");
    this.inputEl = this.querySelector("[data-locator-input]");
    this.suggestionsEl = this.querySelector("[data-locator-suggestions]");
    this.buttonEl = this.querySelector("[data-locator-submit]");
    this.moreButtonEl = this.querySelector("[data-locator-more]");

    this.records = [];
    this.allRecords = [];
    this.markers = [];
    this.map = null;
    this.geocoder = null;
    this.autocompleteService = null;
    this.placesService = null;
    this.mapsLoadPromise = null;
    this.mapsReady = false;
    this.activeResults = [];
    this.visibleCount = this.pageSize;
    this.resultGeocodeCache = new Map();
    this.autocompleteRequestId = 0;
    this.autocompleteDebounceMs = 300;
    this.autocompleteTimer = null;
    this.autocompleteSessionToken = null;
    this.selectedPlace = null;
    this.searchPointMarker = null;
    // Hard cap: never geocode more store addresses than this per search.
    this.storeGeocodeBudget = this.maxResults;

    await this.loadDataset();
    this.renderInitialState();
    this.bindEvents();
    this.renderMapPlaceholder();
  }

  async loadDataset() {
    const response = await fetch(this.datasetUrl, { credentials: "same-origin" });
    const payload = await response.json();
    this.allRecords = payload.locations || [];
    this.records = this.allRecords.filter(
      (record) => typeof record.lat === "number" && typeof record.lon === "number"
    );
  }

  renderMapPlaceholder() {
    if (!this.mapEl || this.map) return;
    this.mapEl.classList.add("jit-store-locator-mvp__map--pending");
    if (!this.mapEl.querySelector("[data-locator-map-placeholder]")) {
      const placeholder = document.createElement("div");
      placeholder.className = "jit-store-locator-mvp__map-placeholder";
      placeholder.setAttribute("data-locator-map-placeholder", "");
      placeholder.textContent = this.googleMapsApiKey
        ? "Search to load the map."
        : "Add a Google Maps browser API key to enable the map.";
      this.mapEl.appendChild(placeholder);
    }
  }

  clearMapPlaceholder() {
    if (!this.mapEl) return;
    this.mapEl.classList.remove("jit-store-locator-mvp__map--pending");
    const placeholder = this.mapEl.querySelector("[data-locator-map-placeholder]");
    if (placeholder) placeholder.remove();
  }

  ensureAutocompleteSession() {
    if (!window.google || !google.maps.places || !google.maps.places.AutocompleteSessionToken) {
      return null;
    }
    if (!this.autocompleteSessionToken) {
      this.autocompleteSessionToken = new google.maps.places.AutocompleteSessionToken();
    }
    return this.autocompleteSessionToken;
  }

  resetAutocompleteSession() {
    this.autocompleteSessionToken = null;
  }

  async ensureMapsLibrary() {
    if (!this.googleMapsApiKey) {
      return false;
    }

    if (this.mapsReady) {
      return true;
    }

    if (!this.mapsLoadPromise) {
      this.mapsLoadPromise = (async () => {
        if (!window.google || !window.google.maps) {
          await new Promise((resolve, reject) => {
            const callbackName = `jitStoreLocatorInit_${Date.now()}`;
            window[callbackName] = () => {
              delete window[callbackName];
              resolve();
            };
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.googleMapsApiKey)}&libraries=places&callback=${callbackName}`;
            script.async = true;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        this.geocoder = new google.maps.Geocoder();
        if (google.maps.places && google.maps.places.AutocompleteService) {
          this.autocompleteService = new google.maps.places.AutocompleteService();
        }
        if (google.maps.places && google.maps.places.PlacesService) {
          this.placesAttributionEl = document.createElement("div");
          this.placesService = new google.maps.places.PlacesService(this.placesAttributionEl);
        }
        this.mapsReady = true;
      })().catch((error) => {
        this.mapsLoadPromise = null;
        this.mapsReady = false;
        throw error;
      });
    }

    await this.mapsLoadPromise;
    return this.mapsReady;
  }

  async ensureMap() {
    const ready = await this.ensureMapsLibrary();
    if (!ready) return false;
    if (this.map) return true;

    this.clearMapPlaceholder();
    this.map = new google.maps.Map(this.mapEl, {
      center: { lat: 39.5, lng: -98.35 },
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    return true;
  }

  bindEvents() {
    this.inputEl.addEventListener("focus", () => {
      // Warm Maps/Places on focus so first suggestions are responsive,
      // but do not instantiate Dynamic Maps until a search needs the map.
      this.ensureMapsLibrary().catch(() => {});
    });
    this.inputEl.addEventListener("input", () => {
      this.selectedPlace = null;
      this.onInputChange();
    });
    this.inputEl.addEventListener("blur", () => {
      window.setTimeout(() => this.hideSuggestions(), 150);
    });
    if (this.moreButtonEl) {
      this.moreButtonEl.addEventListener("click", async () => {
        this.visibleCount += this.pageSize;
        // Do not geocode additional stores on "Load more" — emergency cost cap.
        this.renderResults(this.activeResults, this.lastSearchedPoint || null);
      });
    }
    this.formEl.addEventListener("submit", async (event) => {
      event.preventDefault();
      this.hideSuggestions();
      const query = this.inputEl.value.trim();
      if (!query) {
        this.renderInitialState();
        return;
      }

      this.buttonEl.disabled = true;
      try {
        await this.runSearch(query);
      } finally {
        this.buttonEl.disabled = false;
      }
    });
  }

  async runSearch(query) {
    const mapsReady = await this.ensureMapsLibrary();

    let searchLat = null;
    let searchLng = null;
    let components = null;

    if (this.selectedPlace && this.selectedPlace.location) {
      searchLat = this.selectedPlace.location.lat;
      searchLng = this.selectedPlace.location.lng;
      components = this.selectedPlace.components || {};
      this.statusEl.textContent = `Showing nearest stores to "${query}".`;
      this.selectedPlace = null;
      this.resetAutocompleteSession();
    } else if (mapsReady && this.geocoder) {
      const geocoded = await this.geocodeQuery(query);
      this.resetAutocompleteSession();
      if (geocoded) {
        searchLat = geocoded.location.lat();
        searchLng = geocoded.location.lng();
        components = geocoded.components;
      }
    } else {
      this.resetAutocompleteSession();
    }

    if (searchLat != null && searchLng != null) {
      if (this.records.length) {
        this.activeResults = this.findNearest(searchLat, searchLng);
        this.visibleCount = this.pageSize;
        await this.ensureMap();
        this.renderResults(this.activeResults, { lat: searchLat, lng: searchLng });
        return;
      }

      let fallback = this.findMatchesFromGeocodedComponents(components || {});
      if (!fallback.length) {
        fallback = this.findTextMatches(query);
      }
      this.activeResults = await this.ensureResultCoordinates(fallback, this.storeGeocodeBudget);
      this.activeResults = this.sortByDistance(this.activeResults, searchLat, searchLng);
      this.visibleCount = this.pageSize;
      await this.ensureMap();
      this.renderResults(this.activeResults, { lat: searchLat, lng: searchLng });
      return;
    }

    const fallback = this.findTextMatches(query);
    this.activeResults = await this.ensureResultCoordinates(fallback, this.storeGeocodeBudget);
    this.visibleCount = this.pageSize;
    if (this.activeResults.some((record) => typeof record.lat === "number" && typeof record.lon === "number")) {
      await this.ensureMap();
    }
    this.renderResults(this.activeResults, null);
  }

  onInputChange() {
    const query = this.inputEl.value.trim();
    if (this.autocompleteTimer) {
      window.clearTimeout(this.autocompleteTimer);
      this.autocompleteTimer = null;
    }

    if (query.length < 3) {
      this.hideSuggestions();
      return;
    }

    this.autocompleteTimer = window.setTimeout(() => {
      this.fetchAutocompleteSuggestions(query);
    }, this.autocompleteDebounceMs);
  }

  async fetchAutocompleteSuggestions(query) {
    const ready = await this.ensureMapsLibrary();
    if (!ready || !this.autocompleteService) {
      this.hideSuggestions();
      return;
    }

    const requestId = ++this.autocompleteRequestId;
    const sessionToken = this.ensureAutocompleteSession();
    const request = {
      input: query,
      componentRestrictions: { country: "us" },
      types: ["geocode"],
    };
    if (sessionToken) {
      request.sessionToken = sessionToken;
    }

    const predictions = await new Promise((resolve) => {
      this.autocompleteService.getPlacePredictions(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          resolve(results);
        } else {
          resolve([]);
        }
      });
    });

    if (requestId !== this.autocompleteRequestId) return;
    this.renderSuggestions(predictions);
  }

  renderSuggestions(predictions) {
    if (!this.suggestionsEl) return;
    this.suggestionsEl.innerHTML = "";
    if (!predictions.length) {
      this.hideSuggestions();
      return;
    }

    predictions.slice(0, 5).forEach((prediction) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "jit-store-locator-mvp__suggestion";
      button.textContent = prediction.description;
      button.addEventListener("mousedown", (event) => {
        // Prevent input blur from hiding suggestions before click handlers run.
        event.preventDefault();
      });
      button.addEventListener("click", () => {
        this.selectSuggestion(prediction);
      });
      this.suggestionsEl.appendChild(button);
    });

    this.suggestionsEl.hidden = false;
  }

  async selectSuggestion(prediction) {
    this.inputEl.value = prediction.description;
    this.hideSuggestions();

    const details = await this.resolvePlaceDetails(prediction.place_id);
    if (details) {
      this.selectedPlace = details;
      if (details.formattedAddress) {
        this.inputEl.value = details.formattedAddress;
      }
    } else {
      this.selectedPlace = null;
    }

    this.resetAutocompleteSession();
    this.formEl.requestSubmit();
  }

  resolvePlaceDetails(placeId) {
    if (!this.placesService || !placeId) {
      return Promise.resolve(null);
    }

    const sessionToken = this.autocompleteSessionToken;
    const request = {
      placeId,
      fields: ["geometry", "formatted_address", "address_component", "name"],
    };
    if (sessionToken) {
      request.sessionToken = sessionToken;
    }

    return new Promise((resolve) => {
      this.placesService.getDetails(request, (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place || !place.geometry || !place.geometry.location) {
          resolve(null);
          return;
        }

        resolve({
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          },
          formattedAddress: place.formatted_address || place.name || "",
          components: this.extractAddressComponents({
            address_components: place.address_components || [],
          }),
        });
      });
    });
  }

  hideSuggestions() {
    if (!this.suggestionsEl) return;
    this.suggestionsEl.hidden = true;
    this.suggestionsEl.innerHTML = "";
  }

  findTextMatches(query) {
    const normalized = query.toLowerCase();
    return this.allRecords.filter((record) =>
      (record.searchText || "").toLowerCase().includes(normalized)
    );
  }

  findMatchesFromGeocodedComponents(components) {
    const postalCode = (components.postalCode || "").trim();
    const city = (components.city || "").trim().toLowerCase();
    const state = (components.state || "").trim().toLowerCase();

    let results = [];

    if (postalCode) {
      results = this.allRecords.filter((record) => (record.postalCode || "").trim() === postalCode);
    }

    if (!results.length && city) {
      results = this.allRecords.filter((record) => {
        const recordCity = (record.city || "").trim().toLowerCase();
        const recordState = (record.state || "").trim().toLowerCase();
        return recordCity === city && (!state || recordState === state);
      });
    }

    if (!results.length && state) {
      results = this.allRecords.filter((record) => (record.state || "").trim().toLowerCase() === state);
    }

    return results;
  }

  async geocodeQuery(query) {
    return new Promise((resolve) => {
      this.geocoder.geocode({ address: query }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          this.statusEl.textContent = `Showing nearest stores to "${query}".`;
          resolve({
            location: results[0].geometry.location,
            components: this.extractAddressComponents(results[0]),
          });
        } else {
          this.statusEl.textContent = `Could not geocode "${query}". Showing text-matched results instead.`;
          resolve(null);
        }
      });
    });
  }

  extractAddressComponents(result) {
    const components = {
      postalCode: "",
      city: "",
      state: "",
    };

    (result.address_components || []).forEach((component) => {
      const types = component.types || [];
      if (types.includes("postal_code")) {
        components.postalCode = component.long_name || "";
      }
      if (types.includes("locality") || types.includes("postal_town") || types.includes("sublocality") || types.includes("sublocality_level_1")) {
        if (!components.city) {
          components.city = component.long_name || "";
        }
      }
      if (types.includes("administrative_area_level_1")) {
        components.state = component.short_name || "";
      }
    });

    return components;
  }

  async geocodeAddress(address) {
    if (!this.geocoder || !address) return null;
    if (this.resultGeocodeCache.has(address)) {
      return this.resultGeocodeCache.get(address);
    }

    const result = await new Promise((resolve) => {
      this.geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          resolve({
            lat: results[0].geometry.location.lat(),
            lon: results[0].geometry.location.lng(),
          });
        } else {
          resolve(null);
        }
      });
    });

    if (result) {
      this.resultGeocodeCache.set(address, result);
    }
    return result;
  }

  async ensureResultCoordinates(results, limit = this.storeGeocodeBudget) {
    if (!this.geocoder) return results;

    const hydrated = [];
    let geocodeBudget = Math.max(0, Number(limit) || 0);

    for (const record of results) {
      if (typeof record.lat === "number" && typeof record.lon === "number") {
        hydrated.push(record);
        continue;
      }

      if (geocodeBudget <= 0) {
        // Keep ungeocoded matches in the list; skip billable Geocoding.
        hydrated.push(record);
        continue;
      }

      geocodeBudget -= 1;
      const address = `${record.address1}, ${record.city}, ${record.state} ${record.postalCode}, ${record.country || "USA"}`;
      const coords = await this.geocodeAddress(address);
      if (coords) {
        hydrated.push({
          ...record,
          lat: coords.lat,
          lon: coords.lon,
        });
      } else {
        hydrated.push(record);
      }
    }

    return hydrated;
  }

  findNearest(lat, lon) {
    return [...this.records]
      .map((record) => ({
        ...record,
        distanceMiles: this.haversineMiles(lat, lon, record.lat, record.lon),
      }))
      .sort((a, b) => a.distanceMiles - b.distanceMiles);
  }

  sortByDistance(records, lat, lon) {
    const withCoords = [];
    const withoutCoords = [];

    records.forEach((record) => {
      if (typeof record.lat === "number" && typeof record.lon === "number") {
        withCoords.push({
          ...record,
          distanceMiles: this.haversineMiles(lat, lon, record.lat, record.lon),
        });
      } else {
        withoutCoords.push(record);
      }
    });

    withCoords.sort((a, b) => a.distanceMiles - b.distanceMiles);
    return [...withCoords, ...withoutCoords];
  }

  haversineMiles(lat1, lon1, lat2, lon2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const r = 3958.8;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * r * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  renderInitialState() {
    const seed = this.records.length ? this.records : this.allRecords;
    this.activeResults = seed;
    this.visibleCount = this.pageSize;
    if (!this.statusEl.textContent) {
      this.statusEl.textContent = this.records.length
        ? "Showing prototype sample locations."
        : "Showing dataset preview. Search works, but nearest-map behavior requires geocoded store coordinates.";
    }
    this.renderResults(this.activeResults, null);
  }

  clearMarkers() {
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
    if (this.searchPointMarker) {
      this.searchPointMarker.setMap(null);
      this.searchPointMarker = null;
    }
  }

  renderResults(results, searchedPoint) {
    this.lastSearchedPoint = searchedPoint;
    this.resultsEl.innerHTML = "";
    this.clearMarkers();

    if (!results.length) {
      if (this.summaryEl) this.summaryEl.textContent = "Showing 0 results.";
      if (this.moreButtonEl) this.moreButtonEl.hidden = true;
      this.resultsEl.innerHTML = `<div class="jit-store-locator-mvp__empty">No matching stores found in the current dataset.</div>`;
      if (this.map) {
        this.map.setCenter({ lat: 39.5, lng: -98.35 });
        this.map.setZoom(4);
      }
      return;
    }

    const visibleResults = results.slice(0, this.visibleCount);
    if (this.summaryEl) {
      this.summaryEl.textContent = `Showing ${Math.min(visibleResults.length, results.length)} of ${results.length} results.`;
    }
    if (this.moreButtonEl) {
      this.moreButtonEl.hidden = visibleResults.length >= results.length;
    }

    const canMapResults = this.map && visibleResults.some((record) => typeof record.lat === "number" && typeof record.lon === "number");
    const bounds = canMapResults ? new google.maps.LatLngBounds() : null;

    if (canMapResults && searchedPoint) {
      this.searchPointMarker = new google.maps.Marker({
        position: searchedPoint,
        map: this.map,
        title: "Search location",
      });
      bounds.extend(searchedPoint);
    }

    visibleResults.forEach((record) => {
      const resultEl = document.createElement("article");
      resultEl.className = "jit-store-locator-mvp__result";
      resultEl.innerHTML = `
        <h3>${record.name}</h3>
        <p class="jit-store-locator-mvp__result-meta">${record.address1}${record.address2 ? `, ${record.address2}` : ""}<br>${record.city}, ${record.state} ${record.postalCode}</p>
        ${typeof record.distanceMiles === "number" ? `<p class="jit-store-locator-mvp__result-distance">${record.distanceMiles.toFixed(1)} miles away</p>` : ""}
        <div class="jit-store-locator-mvp__result-actions">
          <a class="jit-store-locator-mvp__link" target="_blank" rel="noopener noreferrer" href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${record.address1}, ${record.city}, ${record.state} ${record.postalCode}`)}">Directions</a>
          ${record.url ? `<a class="jit-store-locator-mvp__link" target="_blank" rel="noopener noreferrer" href="${record.url.startsWith("http") ? record.url : `https://${record.url}`}">Website</a>` : ""}
        </div>
      `;
      this.resultsEl.appendChild(resultEl);

      if (canMapResults && typeof record.lat === "number" && typeof record.lon === "number") {
        const marker = new google.maps.Marker({
          position: { lat: record.lat, lng: record.lon },
          map: this.map,
          title: record.name,
        });
        const infoWindow = new google.maps.InfoWindow({
          content: `<strong>${record.name}</strong><br>${record.address1}<br>${record.city}, ${record.state} ${record.postalCode}`,
        });
        marker.addListener("click", () => infoWindow.open({ anchor: marker, map: this.map }));
        this.markers.push(marker);
        bounds.extend({ lat: record.lat, lng: record.lon });
      }
    });

    if (canMapResults && bounds) {
      this.map.fitBounds(bounds, 40);
    } else if (this.map && searchedPoint) {
      this.map.setCenter(searchedPoint);
      this.map.setZoom(11);
    } else if (this.map) {
      this.map.setCenter({ lat: 39.5, lng: -98.35 });
      this.map.setZoom(4);
    }
  }
}

if (!customElements.get("jit-store-locator-mvp")) {
  customElements.define("jit-store-locator-mvp", JitStoreLocatorMvp);
}
